import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { BottomSheet, Button, Chip, DateField, useSnackbar } from '@sauda/ui/kit';
import { api } from '../../lib/api.js';
import { istDateInput } from '../../lib/money-input.js';
import type { PropertyView } from '../../lib/types.js';

const STATUSES = ['available', 'on_hold', 'let_out_or_sold'] as const;

/** Mark a flat available, on hold or let out, with the day it happened. Used from the list and the flat. */
export function StatusSheet({
  property,
  onClose,
  onDone,
}: {
  property: PropertyView | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const snack = useSnackbar();
  const [status, setStatus] = useState<(typeof STATUSES)[number] | null>(null);
  const [on, setOn] = useState(istDateInput());
  const current =
    status ?? (property?.status === 'archived' ? 'available' : property?.status) ?? 'available';
  const save = useMutation({
    mutationFn: () =>
      api(`/properties/${property?.id ?? ''}/status`, {
        method: 'POST',
        body: current === 'available' ? { status: current } : { status: current, on },
      }),
    onSuccess: () => {
      setStatus(null);
      onDone();
      onClose();
    },
    onError: () => snack({ text: t('property.generic') }),
  });
  return (
    <BottomSheet
      open={property !== null}
      onClose={onClose}
      label={t('property.changeStatus')}
      title={property?.title ?? t('property.changeStatus')}
      closeLabel={t('app.close')}
      actions={
        <Button
          size="lg"
          block
          loading={save.isPending}
          disabled={property === null || current === property.status}
          onClick={() => save.mutate()}
        >
          {t('property.saveStatus')}
        </Button>
      }
    >
      <div className="chips-row" role="group" aria-label={t('property.statusLabel')}>
        {STATUSES.map((s) => (
          <Chip key={s} selected={current === s} onClick={() => setStatus(s)}>
            {t(`property.status_${s}`)}
          </Chip>
        ))}
      </div>
      {current === 'available' ? null : (
        <DateField label={t('property.statusOn')} value={on} onChange={setOn} />
      )}
    </BottomSheet>
  );
}
